import {CommerceAPI} from 'commerce-api'
import {computed, flow, makeObservable, observable, action} from 'mobx'
import {RootStore} from './RootStore'
import {PromiseFlow, StoreModel} from './utils'
import {Status, StatusMap} from '../types/commerce'
import { getAppOrigin } from '@salesforce/pwa-kit-react-sdk/utils/url'

export interface PromotionResponse {
  c_contentAsset: string
  groups?: any[]
  c_groupLabels?: string[]
  t?: string
  c_isMealDealLanding: boolean
  c_promotionOverrideMessage: string
  c_rulesetJSON: string
  c_customerIsEligible?: boolean
  c_isUpcoming?: boolean,
  c_isExpired?: boolean,
  calloutMsg: string
  id: string
  name: string
  startDate: string
  endDate: string,
  price?: string
  _type: 'promotion'
  details: string
}

export interface NormalizedPromotion extends Omit<PromotionResponse, '_type' | 'c_rulesetJSON'> {
  productIds: string[]
  discountValue: string[]
  discountName: string
  groups: NormalizedPromotionGroup[]
  groupLabels?: string[]
  price?: string
  t?: string
}

export interface NormalizedPromotionGroup {
  id?: string
  ids?: string[]
  req?: string[]
  t?: string
}

export class PromotionStore implements StoreModel {
  api: CommerceAPI
  promotions: Record<string, NormalizedPromotion>
  upcomingMealDeals: string[]
  activeMealDeals: string[]
  fetchQueue: { [key: string]: boolean; }
  status: Status

  constructor(rootStore: RootStore, initialState = {promotions: {}, upcomingMealDeals: [], activeMealDeals: [], status: StatusMap.IDLE}) {
    this.promotions = initialState?.promotions
    this.upcomingMealDeals = initialState?.upcomingMealDeals
    this.activeMealDeals = initialState?.activeMealDeals
    this.status = initialState?.status
    this.fetchQueue = {}

    makeObservable(this, {
      status: observable,
      promotions: observable,
      upcomingMealDeals: observable,
      activeMealDeals: observable,
      isRequestProcessing: computed,
      fetchPromotionsByIds: flow.bound,
      fetchMealDeals: flow.bound,
      setStatus: action.bound
    })
    this.api = rootStore.api
  }

  get asJson() {
    return {
      promotions: this.promotions,
      upcomingMealDeals: this.upcomingMealDeals,
      activeMealDeals: this.activeMealDeals
    }
  }

  get isRequestProcessing() {
    return this.status === StatusMap.PENDING
  }

  setStatus(value: Status) {
    this.status = value
  }

  /**
   * Fetches SFCC promotions by ID
   * @param { array } ids List of required IDs
   * @param { boolean } ignoreCache - indicates if we should ignore the cache - useful if always requiring fresh data
   */
  *fetchPromotionsByIds(ids: string[], ignoreCache: boolean): PromiseFlow<any> {
    try {
      this.setStatus(StatusMap.PENDING)

      let nonExistedPromotionIds = []
      if (ignoreCache) {
        nonExistedPromotionIds = ids
      } else {
        // Check if we already have this Promo in the store and De-dupe the list of promos
        // Also exclude here if we're already in process of fetching any of the requested Promos
        nonExistedPromotionIds = ids?.filter((id) => !Object.prototype.hasOwnProperty.call(this.promotions, id) && !Object.prototype.hasOwnProperty.call(this.fetchQueue, id)) ?? []
      }

      // If nothing to fetch, Jog on
      if (!nonExistedPromotionIds.length) return

      // Add any we need to fetch to the fetching queue to prevent future calls hitting it while we're in progress
      nonExistedPromotionIds.forEach((id) => this.fetchQueue[id] = true)

      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Promotions-GetPromotionsByIds`,
        {
          method: 'POST',
          body: JSON.stringify({
            promotionsIds: nonExistedPromotionIds,
          })
        }
      )

      const json: {promotions: PromotionResponse[] } = yield res.json()

      // If we got nothing back, move on
      if (!json.promotions) return

      // Store the promotion in the Store
      for (const promotion of json.promotions as PromotionResponse[]) {
        // Store in store
        this.promotions[promotion.id] = transformPromotion(promotion)
        // Remove from fetching list
        delete this.fetchQueue[promotion.id]
      }
      this.setStatus(StatusMap.DONE)
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      console.error(`Failed to fetch promotion with ids: ${ids}`, error)
    }
  }

  /**
   * Fetches any meal deals that the customer is eligible for
  */
  *fetchMealDeals(): PromiseFlow<any> {
    try {
      this.setStatus(StatusMap.PENDING)
      const res: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Promotions-GetMealDeals`
      )
      const json: {active?: PromotionResponse[], upcoming?: PromotionResponse[] } = yield res.json()
      // Clear existing stored deals
      this.activeMealDeals.length = 0
      this.upcomingMealDeals.length = 0
      if (json?.active && Array.isArray(json?.active)) {
        // Store the promotion in the Store
        for (const promotion of json.active as PromotionResponse[]) {
          // Store in store
          this.promotions[promotion.id] = transformPromotion(promotion)
          if (!this.activeMealDeals.includes(promotion.id) && !this.upcomingMealDeals.includes(promotion.id)) {
            this.activeMealDeals.push(promotion.id)
          }
        }
      }
      if (json?.upcoming && Array.isArray(json?.upcoming)) {
        // Store the promotion in the Store
        for (const promotion of json.upcoming as PromotionResponse[]) {
          // Store in store
          if (!this.activeMealDeals.includes(promotion.id) && !this.upcomingMealDeals.includes(promotion.id)) {
            this.upcomingMealDeals.push(promotion.id)
          }
          this.promotions[promotion.id] = transformPromotion(promotion)
        }
      }
      this.setStatus(StatusMap.DONE)
    } catch (error) {
      this.setStatus(StatusMap.ERROR)
      console.error(`Failed to fetch customer promotions`, error)
    }
  }
}

const transformPromotion = (promotion: PromotionResponse): NormalizedPromotion => {
  const returnPromotion: NormalizedPromotion = {
    id: promotion.id,
    name: promotion.name,
    c_contentAsset: promotion.c_contentAsset,
    c_isMealDealLanding: promotion.c_isMealDealLanding,
    c_promotionOverrideMessage: promotion.c_promotionOverrideMessage,
    c_customerIsEligible: promotion.c_customerIsEligible,
    c_isUpcoming: promotion.c_isUpcoming,
    c_isExpired: promotion.c_isExpired,
    calloutMsg: promotion.calloutMsg,
    startDate: promotion.startDate,
    endDate: promotion.endDate,
    groupLabels: promotion.c_groupLabels,
    price: promotion.price,
    details: promotion.details,
    discountName: '',
    discountValue: [],
    groups: [],
    productIds: [],
    t: '',
  }

  // Try set rule set data
  try {
    const ruleset = promotion.c_rulesetJSON && JSON.parse(promotion.c_rulesetJSON);
    if (ruleset) {
      returnPromotion.groups = ruleset.groups
      returnPromotion.discountName = ruleset.discountName
      returnPromotion.discountValue = ruleset.discountValue
      returnPromotion.startDate = ruleset.startDate
      returnPromotion.endDate = ruleset.endDate
      returnPromotion.groups = ruleset.groups
      returnPromotion.productIds = ruleset.groups.flatMap(({ids}: {ids: string[]}) => ids)
      returnPromotion.t = ruleset.t
      returnPromotion.price = ruleset.price
    }
  } catch (error) {
    console.error(error)
  }

  return returnPromotion
}

export const getPromoExpiryText = (startDate?: string, endDate?: string) => {
  let expiryMessage= ''
  if (startDate && endDate) {
    expiryMessage = `Offer valid for delivery from ${new Date(startDate).toLocaleDateString()} until ${new Date(endDate).toLocaleDateString()}. `
  } else if (endDate) {
    expiryMessage = `Offer valid for delivery until ${new Date(endDate).toLocaleDateString()}. `
  }

  return `${expiryMessage}Offer subject to availability.`
}
